import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, Edit2, Check, X, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS_COLORS = {
  draft: { bg: "#F5F3EF", text: "#57534E" },
  sent: { bg: "#DBEAFE", text: "#1D4ED8" },
  paid: { bg: "#D1FAE5", text: "#059669" },
  overdue: { bg: "#FEE2E2", text: "#DC2626" },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => {
      setInvoice(r.data);
      setLineItems(r.data.line_items || []);
      setTaxRate(r.data.tax_rate || 0);
      setDueDate(r.data.due_date || "");
      setNotes(r.data.notes || "");
    }).catch(() => navigate("/invoices")).finally(() => setLoading(false));
  }, [id]);

  const computeTotals = (items, rate) => {
    const subtotal = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const tax = subtotal * (rate / 100);
    return { subtotal: subtotal.toFixed(2), tax: tax.toFixed(2), total: (subtotal + tax).toFixed(2) };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/invoices/${id}`, {
        line_items: lineItems,
        tax_rate: parseFloat(taxRate),
        due_date: dueDate,
        notes,
      });
      setInvoice(r.data);
      setEditing(false);
      toast.success("Invoice updated");
    } catch {
      toast.error("Failed to update invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post(`/invoices/${id}/send`, { custom_message: customMessage || null });
      setInvoice((prev) => ({ ...prev, status: "sent" }));
      setShowSendDialog(false);
      setCustomMessage("");
      toast.success("Invoice sent successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const r = await api.put(`/invoices/${id}`, { status });
      setInvoice(r.data);
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const updateItem = (idx, field, value) => {
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === "quantity" || field === "rate") {
        updated.amount = (parseFloat(updated.quantity || 0) * parseFloat(updated.rate || 0)).toFixed(2);
      }
      return updated;
    }));
  };

  const addItem = () => setLineItems((prev) => [...prev, { description: "", quantity: 1, rate: 0, amount: 0 }]);
  const removeItem = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));

  if (loading) return <Layout><div className="p-8 text-sm" style={{ color: "#A8A29E" }}>Loading...</div></Layout>;
  if (!invoice) return null;

  const sc = STATUS_COLORS[invoice.status] || { bg: "#F5F3EF", text: "#57534E" };
  const totals = editing ? computeTotals(lineItems, taxRate) : { subtotal: invoice.subtotal?.toFixed(2), tax: invoice.tax_amount?.toFixed(2), total: invoice.total?.toFixed(2) };

  return (
    <Layout>
      <div className="p-8 max-w-4xl" data-testid="invoice-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/invoices")} className="p-1.5 rounded-lg hover:bg-white" style={{ color: "#A8A29E" }}>
              <ArrowLeft size={18} strokeWidth={1.5} />
            </button>
            <div>
              <h1 className="font-serif text-2xl font-bold" style={{ color: "#292524" }}>{invoice.invoice_number}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm" style={{ color: "#57534E" }}>{invoice.client_name}</span>
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>{invoice.status}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setLineItems(invoice.line_items || []); }}><X size={14} /></Button>
                <Button size="sm" disabled={saving} onClick={handleSave} style={{ background: "#292524", color: "#fff" }}><Check size={14} className="mr-1" />{saving ? "Saving..." : "Save"}</Button>
              </>
            ) : (
              <>
                {invoice.status === "draft" && <Button data-testid="edit-invoice-button" variant="outline" size="sm" onClick={() => setEditing(true)}><Edit2 size={14} className="mr-1" />Edit</Button>}
                <Button data-testid="download-pdf-button" variant="outline" size="sm" onClick={handleDownloadPDF}>
                  <Download size={14} className="mr-1" />PDF
                </Button>
                {invoice.status === "draft" && (
                  <Button data-testid="send-invoice-button" size="sm" disabled={sending} onClick={() => setShowSendDialog(true)} style={{ background: "#0284C7", color: "#fff" }}>
                    <Send size={14} className="mr-1" />Send Invoice
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Invoice body */}
        <div className="bg-white rounded-xl border p-8" style={{ borderColor: "#E7E5E4" }}>
          {/* Meta */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>Billed To</p>
              <p className="font-medium" style={{ color: "#292524" }}>{invoice.client_name}</p>
              <p className="text-sm" style={{ color: "#57534E" }}>{invoice.client_email}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>Invoice Details</p>
              <p className="text-sm" style={{ color: "#57534E" }}>Issued: {invoice.created_at?.slice(0, 10)}</p>
              {editing ? (
                <div className="mt-1"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="text-right" /></div>
              ) : (
                <p className="text-sm" style={{ color: "#57534E" }}>Due: {invoice.due_date}</p>
              )}
              {invoice.status !== "draft" && !editing && (
                <div className="mt-2">
                  <select value={invoice.status} onChange={(e) => handleStatusChange(e.target.value)}
                    className="text-xs px-2 py-1 rounded-lg border" style={{ borderColor: "#E7E5E4" }}>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Line items */}
          <table className="w-full mb-4">
            <thead>
              <tr style={{ borderBottom: "1px solid #E7E5E4", borderTop: "1px solid #E7E5E4" }}>
                {["Description", "Qty", "Rate (£)", "Amount (£)"].map((h) => (
                  <th key={h} className="text-left py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E", paddingRight: h === "Description" ? 0 : "1rem" }}>
                    {h}
                  </th>
                ))}
                {editing && <th />}
              </tr>
            </thead>
            <tbody>
              {(editing ? lineItems : invoice.line_items || []).map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #F5F3EF" }}>
                  <td className="py-3 pr-4">
                    {editing ? <Input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="text-sm" /> : <span className="text-sm" style={{ color: "#292524" }}>{item.description}</span>}
                  </td>
                  <td className="py-3 pr-4 w-20">
                    {editing ? <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="text-sm" /> : <span className="text-sm" style={{ color: "#57534E" }}>{item.quantity}</span>}
                  </td>
                  <td className="py-3 pr-4 w-28">
                    {editing ? <Input type="number" value={item.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} className="text-sm" /> : <span className="text-sm" style={{ color: "#57534E" }}>£{parseFloat(item.rate || 0).toFixed(2)}</span>}
                  </td>
                  <td className="py-3 text-sm font-medium" style={{ color: "#292524" }}>£{parseFloat(item.amount || 0).toFixed(2)}</td>
                  {editing && <td><button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-red-50" style={{ color: "#A8A29E" }}><Trash2 size={13} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>

          {editing && (
            <button onClick={addItem} className="flex items-center gap-1.5 text-sm mb-6" style={{ color: "#D97706" }}>
              <Plus size={14} />Add line item
            </button>
          )}

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-56 space-y-2">
              <div className="flex justify-between text-sm" style={{ color: "#57534E" }}>
                <span>Subtotal</span>
                <span>£{totals.subtotal}</span>
              </div>
              {(editing ? parseFloat(taxRate) > 0 : invoice.tax_rate > 0) && (
                <div className="flex justify-between text-sm" style={{ color: "#57534E" }}>
                  <span>Tax ({editing ? taxRate : invoice.tax_rate}%)</span>
                  <span>£{totals.tax}</span>
                </div>
              )}
              {editing && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: "#57534E" }}>Tax Rate (%)</span>
                  <Input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-20 text-sm" />
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2" style={{ borderTop: "1px solid #E7E5E4", color: "#292524" }}>
                <span>Total</span>
                <span>£{totals.total}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {(invoice.notes || editing) && (
            <div className="mt-6 pt-4" style={{ borderTop: "1px solid #E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>Notes</p>
              {editing ? (
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." />
              ) : (
                <p className="text-sm" style={{ color: "#57534E" }}>{invoice.notes}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent data-testid="send-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Send Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm" style={{ color: "#57534E" }}>
              Send <strong>{invoice?.invoice_number}</strong> to <strong>{invoice?.client_email}</strong>
            </p>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>
                Custom Message (optional)
              </Label>
              <Textarea
                data-testid="send-custom-message-input"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personal note to include in the email..."
                className="mt-1.5 resize-none min-h-[80px]"
              />
              <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>
                This note will appear highlighted in the email body.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button
              data-testid="confirm-send-invoice-button"
              onClick={handleSend}
              disabled={sending}
              style={{ background: "#0284C7", color: "#fff" }}
            >
              <Send size={13} className="mr-1.5" />
              {sending ? "Sending..." : "Send Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
